import type { NoticeDataWidgetLayer, NoticeDataWidgetSource, NoticeLayer, NoticeTextGradient } from './types.js';
import { clockWidgetText } from './noticeWidgets.js';
export type LocalTimeSource = Extract<NoticeDataWidgetSource, { kind: 'clock' | 'date' | 'weekday' }>;
export type ClockPart = NonNullable<Extract<NoticeDataWidgetSource, { kind: 'clock' }>['part']>;
export const isLocalTimeSource = (source: NoticeDataWidgetSource): source is LocalTimeSource => ['clock', 'date', 'weekday'].includes(source.kind);
export const isBoWidgetKind = (kind: NoticeDataWidgetSource['kind']): boolean => ['rss', 'notices', 'schedule'].includes(kind);
const two = (value: number) => String(value).padStart(2, '0');
/** CSS font shorthand needs quotes around family names with spaces ("M PLUS 1p"); generic families must stay bare. */
export const cssFontFamily = (family: string | undefined): string =>
  !family ? 'sans-serif' : /^(sans-serif|serif|monospace|system-ui|cursive|fantasy)$/.test(family) ? family : `'${family.replace(/'/g, '')}', sans-serif`;
export function timeWidgetText(source: LocalTimeSource, now = new Date()): string {
  if (source.kind === 'clock') {
    if (source.mode !== 'analog' && source.part === 'hour') return two(source.hour12 ? now.getHours() % 12 || 12 : now.getHours());
    if (source.mode !== 'analog' && source.part === 'minute') return two(now.getMinutes());
    if (source.mode !== 'analog' && source.part === 'secondsTape') return two(now.getSeconds());
    return clockWidgetText(source, now);
  }
  const enWeekday = () => now.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
  if (source.kind === 'weekday') {
    if (source.format === 'en-short') return enWeekday();
    return now.toLocaleDateString('ko-KR', { weekday: source.format });
  }
  if (source.format === 'day') return two(now.getDate());
  if (source.format === 'weekday-day') return `${enWeekday()} ${two(now.getDate())}`;
  if (source.format === 'month-day-weekday') {
    return `${now.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' })} (${now.toLocaleDateString('ko-KR', { weekday: 'short' })})`;
  }
  return now.toLocaleDateString('ko-KR', source.format === 'numeric' ? { year: 'numeric', month: '2-digit', day: '2-digit' }
    : source.format === 'short' ? { month: 'long', day: 'numeric' } : { year: 'numeric', month: 'long', day: 'numeric' });
}
/** Seconds tape motion inside one second: a continuous, constant-speed flow from
 * right to left — the next value is centred exactly on the second boundary, so
 * there is no hold-and-jump. Returns 0 … 1 (fraction of the second elapsed). */
export function secondsTapeProgress(milliseconds: number): number {
  return Math.min(1, Math.max(0, milliseconds / 1000));
}
/** Opacity of a tape entry by its distance (in slots) from the centre: 1 at the
 * centre, 0.5 one slot away, fading to 0 by two and a half slots. */
export function secondsTapeAlpha(distance: number): number {
  const d = Math.abs(distance);
  return d <= 1 ? 1 - 0.5 * d : Math.max(0, 0.5 * (1 - (d - 1) / 1.5));
}
/** Digit pitch of the tape relative to its font size (Figma: 46px glyphs + 18px gap at 48px). */
export const SECONDS_TAPE_PITCH = 4 / 3;
function gradientFill(ctx: CanvasRenderingContext2D, gradient: NoticeTextGradient, x: number, y: number, width: number, height: number): CanvasGradient {
  const angle = ((gradient.angle ?? 180) % 360) * Math.PI / 180;
  // CSS gradient line: length so the corners map to 0 and 1.
  const length = Math.abs(width * Math.sin(angle)) + Math.abs(height * Math.cos(angle));
  const cx = x + width / 2, cy = y + height / 2, dx = Math.sin(angle) * length / 2, dy = -Math.cos(angle) * length / 2;
  const fill = ctx.createLinearGradient(cx - dx, cy - dy, cx + dx, cy + dy);
  const start = Math.min(gradient.start ?? 0, gradient.end ?? 1), end = Math.max(gradient.start ?? 0, gradient.end ?? 1);
  fill.addColorStop(start, gradient.from); fill.addColorStop(end, gradient.to);
  return fill;
}
/** Same local-time renderer for editor, thumbnails and Player. */
export function drawTimeWidget(ctx: CanvasRenderingContext2D, layer: NoticeDataWidgetLayer, now = new Date()): void {
  if (!isLocalTimeSource(layer.source)) return;
  ctx.save(); ctx.beginPath(); ctx.rect(layer.x, layer.y, layer.width, layer.height); ctx.clip();
  if (layer.backgroundColor) { ctx.fillStyle = layer.backgroundColor; ctx.fillRect(layer.x, layer.y, layer.width, layer.height); }
  ctx.fillStyle = layer.color; ctx.strokeStyle = layer.color;
  const family = cssFontFamily(layer.fontFamily);
  const weight = layer.fontWeight ?? (layer.source.kind === 'clock' ? 500 : 400);
  const part = layer.source.kind === 'clock' && layer.source.mode !== 'analog' ? layer.source.part : undefined;
  if (layer.textGradient && typeof ctx.createLinearGradient === 'function') ctx.fillStyle = gradientFill(ctx, layer.textGradient, layer.x, layer.y, layer.width, layer.height);
  if (layer.source.kind === 'clock' && layer.source.mode === 'analog') {
    ctx.fillStyle = layer.color;
    const radius = Math.min(layer.width, layer.height) * 0.41;
    ctx.translate(layer.x + layer.width / 2, layer.y + layer.height / 2); ctx.lineCap = 'round';
    ctx.lineWidth = Math.max(1, radius * 0.012);
    ctx.beginPath(); ctx.arc(0, 0, radius, 0, Math.PI * 2); ctx.stroke();
    for (let hour = 0; hour < 12; hour++) {
      const major = hour % 3 === 0, angle = hour * Math.PI / 6;
      const outer = radius * 0.9, inner = radius * (major ? 0.73 : 0.8);
      ctx.lineWidth = Math.max(1, radius * (major ? 0.027 : 0.013));
      ctx.beginPath(); ctx.moveTo(Math.sin(angle) * inner, -Math.cos(angle) * inner);
      ctx.lineTo(Math.sin(angle) * outer, -Math.cos(angle) * outer); ctx.stroke();
    }
    const minutes = now.getMinutes() + now.getSeconds() / 60;
    const hand = (angle: number, length: number, width: number) => {
      ctx.save(); ctx.rotate(angle); ctx.lineWidth = width; ctx.beginPath(); ctx.moveTo(0, radius * 0.05); ctx.lineTo(0, -length); ctx.stroke(); ctx.restore();
    };
    hand(((now.getHours() % 12 + minutes / 60) / 12) * Math.PI * 2, radius * 0.6, Math.max(2, radius * 0.045));
    hand(minutes / 60 * Math.PI * 2, radius * 0.7, Math.max(1.5, radius * 0.027));
    ctx.beginPath(); ctx.arc(0, 0, Math.max(2, radius * 0.027), 0, Math.PI * 2); ctx.fill();
  } else if (part === 'secondsTape') {
    // Seven slots are drawn so both fading edges stay smooth while sliding.
    // Glyph ascenders overshoot the em box, so keep 1.3× line room inside the clip.
    const size = Math.min(layer.fontSize, layer.height / 1.3), pitch = size * SECONDS_TAPE_PITCH;
    const progress = secondsTapeProgress(now.getMilliseconds()), second = now.getSeconds();
    const cx = layer.x + layer.width / 2, cy = layer.y + layer.height / 2;
    ctx.font = `${weight} ${size}px ${family}`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const base = ctx.globalAlpha;
    for (let slot = -3; slot <= 3; slot++) {
      const alpha = secondsTapeAlpha(slot - progress);
      if (alpha <= 0) continue;
      ctx.globalAlpha = base * alpha;
      ctx.fillText(two((second + slot + 60) % 60), cx + (slot - progress) * pitch, cy);
    }
    ctx.globalAlpha = base;
  } else {
    const lines = timeWidgetText(layer.source, now).split('\n');
    // Split hour/minute digits are set solid (leading-none) so a 350px box shows 350px glyphs.
    const leading = part ? 1 : 1.3;
    let size = Math.min(layer.fontSize, layer.height / (lines.length * leading));
    ctx.font = `${weight} ${size}px ${family}`;
    const longest = Math.max(...lines.map(line => ctx.measureText(line).width));
    if (longest > layer.width * 0.98) size *= layer.width * 0.98 / longest;
    ctx.font = `${weight} ${size}px ${family}`; ctx.textAlign = layer.align ?? 'center'; ctx.textBaseline = 'middle';
    const x = layer.align === 'left' ? layer.x : layer.align === 'right' ? layer.x + layer.width : layer.x + layer.width / 2;
    lines.forEach((line, index) => ctx.fillText(line, x, layer.y + layer.height / 2 + (index - (lines.length - 1) / 2) * size * leading));
  }
  ctx.restore();
}
/** Widget font families that ship with the apps; preload them so the first
 * canvas frame is not drawn with a fallback face. Safe without a DOM. */
export function preloadWidgetFonts(layers: readonly NoticeLayer[]): void {
  const fonts = (globalThis as { document?: { fonts?: { load(font: string): Promise<unknown> } } }).document?.fonts;
  if (!fonts || typeof fonts.load !== 'function') return;
  for (const layer of layers) {
    if ((layer.kind !== 'dataWidget' && layer.kind !== 'text') || !layer.fontFamily || /^(sans-serif|serif|monospace)$/.test(layer.fontFamily)) continue;
    const weight = layer.fontWeight ?? (layer.kind === 'dataWidget' && layer.source.kind === 'clock' ? 500 : 400);
    void fonts.load(`${weight} 32px '${layer.fontFamily}'`).catch(() => {});
  }
}
/** Split only in the editable draft; approved/playing versions remain unchanged until saved. */
export function splitLegacyClockLayers(layers: NoticeLayer[]): NoticeLayer[] {
  const ids = new Set(layers.map(layer => layer.id));
  const id = (original: string, suffix: string) => { const base = `${original.slice(0, 110)}-${suffix}`; let next = base, n = 1; while (ids.has(next)) next = `${base}-${n++}`; ids.add(next); return next; };
  return layers.flatMap(layer => {
    if (layer.kind !== 'dataWidget' || layer.source.kind !== 'clock' || !layer.source.showDate && !layer.source.showWeekday) return [layer];
    const { showDate, showWeekday, ...source } = layer.source;
    const lineHeight = Math.max(1, Math.min(layer.height / 2, layer.fontSize * 1.3));
    const centerY = layer.y + layer.height / 2;
    const result: NoticeDataWidgetLayer[] = [{ ...layer, source, y: centerY - lineHeight, height: lineHeight }];
    const dateWidth = showWeekday && showDate ? Math.max(1, layer.width * 0.76) : layer.width;
    if (showDate) result.push({ ...layer, id: id(layer.id, 'date'), name: '날짜', title: '날짜', source: { kind: 'date', format: 'long' }, y: centerY, width: dateWidth, height: lineHeight });
    if (showWeekday) result.push({ ...layer, id: id(layer.id, 'weekday'), name: '요일', title: '요일', source: { kind: 'weekday', format: 'long' },
      x: layer.x + (showDate ? dateWidth : 0), y: centerY, width: showDate ? Math.max(1, layer.width - dateWidth) : layer.width, height: lineHeight });
    if (layer.rotation) for (const item of result) {
      const cx = layer.x + layer.width / 2, cy = centerY, dx = item.x + item.width / 2 - cx, dy = item.y + item.height / 2 - cy, a = layer.rotation * Math.PI / 180;
      item.x += dx * Math.cos(a) - dy * Math.sin(a) - dx; item.y += dx * Math.sin(a) + dy * Math.cos(a) - dy;
    }
    return result;
  });
}
